import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import { Issuer } from "src/utils/auth";
import toast from "react-hot-toast";

import { getCommonError } from "src/utils/get-common-error";

const STORAGE_KEY = "accessToken";
const SIGN_IN_REASON = "signInReason";

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SIGN_IN"] = "SIGN_IN";
  ActionType["SIGN_UP"] = "SIGN_UP";
  ActionType["SIGN_OUT"] = "SIGN_OUT";
  ActionType["RESET_PASSWORD"] = "RESET_PASSWORD";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  RESET_PASSWORD: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = window.sessionStorage.getItem(STORAGE_KEY);
      if (accessToken) {
        const user = await authApi.me({ accessToken });

        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      const { message } = getCommonError(error);
      toast.error(message);
      console.error(error);

      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [dispatch]);

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const signIn = useCallback(
    async (username, password, reason) => {
      const { accessToken } = await authApi.signIn({ username, password });

      if (accessToken === "reset") {
        try {
          // 계정 초기화(독립적으로 동작하므로 비동기로 처리)
          authApi.sendLog({
            cmd: "auth",
            id: username,
            username: username,
            action: "reset",
            reason: `Account reset by administrator. Password change required.(No ID logged in yet)`,
          });
        } catch (error) {
          console.error(error);
        }

        const error = new Error(
          "관리자에 의해 초기화된 계정입니다. 비밀번호를 변경해주세요.",
        );
        error.status = 401;
        error.code = "ACCOUNT_RESET";
        throw error;
      }

      if (accessToken === "inactive") {
        try {
          // 계정 비활성화(독립적으로 동작하므로 비동기로 처리)
          authApi.sendLog({
            cmd: "auth",
            id: username,
            username: username,
            action: "inactive",
            reason: `Account inactivated after 5+ incorrect password attempts.(No ID logged in yet)`,
          });
        } catch (error) {
          console.error(error);
        }

        const error = new Error(
          "비밀번호가 5회 이상 잘못 입력되어 계정이 비활성화 되었습니다. 계정 활성화는 관리자에게 문의하세요.",
        );
        error.status = 401;
        error.code = "ACCOUNT_INACTIVE";
        throw error;
      }

      if (accessToken === "expire") {
        try {
          // 비밀번호 만기(독립적으로 동작하므로 비동기로 처리)
          authApi.sendLog({
            cmd: "auth",
            id: username,
            username: username,
            action: "expire",
            reason: `Password expired after 30 days. Password change required.(No ID logged in yet)`,
          });
        } catch (error) {
          console.error(error);
        }

        const error = new Error(
          "보안을 위해 비밀번호를 30일마다 변경해야합니다. 입력하신 비밀번호는 30일이 지났으니 변경해주세요.",
        );
        error.status = 401;
        error.code = "ACCOUNT_EXPIRE";
        throw error;
      }

      const user = await authApi.me({ accessToken });

      try {
        // 로그인 성공(독립적으로 동작하므로 비동기로 처리)
        authApi.sendLog({
          cmd: "auth",
          id: user.id,
          username: user.username,
          action: "signIn",
          reason: `Sign in successful. Sign in reason: ${reason}`,
        });
      } catch (error) {
        console.error(error);
      }

      sessionStorage.setItem(STORAGE_KEY, accessToken);
      sessionStorage.setItem(SIGN_IN_REASON, reason);

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });
    },
    [dispatch],
  );

  const signUp = useCallback(
    async (username, password, name, role, department) => {
      const { accessToken } = await authApi.signUp({
        username,
        password,
        name,
        role,
        department,
      });
      const user = await authApi.me({ accessToken });

      try {
        // 회원가입 성공(독립적으로 동작하므로 비동기로 처리)
        authApi.sendLog({
          cmd: "auth",
          id: user.id,
          username: user.username,
          action: "signUp",
          reason: `Sign up successful with super role.`,
        });
      } catch (error) {
        console.error(error);
      }

      sessionStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });
    },
    [dispatch],
  );

  const signOut = useCallback(
    async (id, username) => {
      try {
        const reason = sessionStorage.getItem(SIGN_IN_REASON);

        // 로그아웃 성공(독립적으로 동작하므로 비동기로 처리)
        authApi.sendLog({
          cmd: "auth",
          id,
          username,
          action: "signOut",
          reason: `Sign out successful. Sign in reason: ${reason}`,
        });
      } catch (error) {
        console.error(error);
      }

      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SIGN_IN_REASON);
      dispatch({ type: ActionType.SIGN_OUT });
    },
    [dispatch],
  );

  const resetPassword = useCallback(
    async (username, oldPassword, newPassword) => {
      const { accessToken } = await authApi.resetPassword({
        username,
        oldPassword,
        newPassword,
      });
      const user = await authApi.me({ accessToken });

      try {
        // 비밀번호 변경 성공(독립적으로 동작하므로 비동기로 처리)
        authApi.sendLog({
          cmd: "auth",
          id: user.id,
          username: user.username,
          action: "changePassword",
          reason: `Password change successful`,
        });
      } catch (error) {
        console.error(error);
      }

      sessionStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: ActionType.RESET_PASSWORD,
        payload: {
          user,
        },
      });
    },
    [dispatch],
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
